//////////////////////////////////////
// PAGINATION COMPONENT
//
// Shared pagination utility for blocks.
//
// Usage:
//   import { renderPagination } from '../../components/Pagination/pagination.js';
//
//   renderPagination({
//     container:   block.querySelector('.pagination'),
//     currentPage: 1,
//     totalItems:  100,
//     perPage:     10,
//     label:       'Providers',
//     onPageChange(page) { state.page = page; render(); }
//   });

/**
 * Build an array of page numbers with ellipsis.
 * Returns e.g. [1, '...', 4, 5, 6, '...', 12]
 */
export function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

/**
 * Render pagination controls into a .pagination container.
 *
 * @param {Object} opts
 * @param {Element}  opts.container    - The .pagination element
 * @param {number}   opts.currentPage  - Current active page (1-indexed)
 * @param {number}   opts.totalItems   - Total number of items
 * @param {number}   opts.perPage      - Items per page
 * @param {string}   opts.label        - Noun for the info text (e.g. 'Providers', 'Clinics')
 * @param {Function} opts.onPageChange - Called with new page number on click
 * @param {boolean}  [opts.scroll]     - Scroll to container on page change (default true)
 */
export function renderPagination({ container, currentPage, totalItems, perPage, label, onPageChange, scroll = true }) {
  if (!container) return;

  const info     = container.querySelector('.pagination-info');
  const controls = container.querySelector('.pagination-controls');
  if (!info || !controls) return;

  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));

  // Update info text
  if (totalItems === 0) {
    info.textContent = `No ${label} found`;
  } else {
    const start = (currentPage - 1) * perPage;
    const showStart = start + 1;
    const showEnd   = Math.min(start + perPage, totalItems);
    info.textContent = `Showing ${showStart}-${showEnd} of ${totalItems} ${label}`;
  }

  // Hide controls when only one page
  if (totalPages <= 1) {
    controls.innerHTML = '';
    return;
  }

  // Build buttons
  controls.innerHTML = '';

  const go = (page) => {
    onPageChange(page);
    if (scroll) container.closest('section, [class*="block"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Previous
  const prevBtn = document.createElement('button');
  prevBtn.textContent = 'Previous';
  prevBtn.disabled = currentPage === 1;
  prevBtn.addEventListener('click', () => go(currentPage - 1));
  controls.appendChild(prevBtn);

  // Page numbers
  const pages = buildPageNumbers(currentPage, totalPages);
  pages.forEach(p => {
    const btn = document.createElement('button');
    if (p === '...') {
      btn.textContent = '...';
      btn.classList.add('ellipsis');
    } else {
      btn.textContent = p;
      if (p === currentPage) btn.classList.add('active');
      btn.addEventListener('click', () => go(p));
    }
    controls.appendChild(btn);
  });

  // Next
  const nextBtn = document.createElement('button');
  nextBtn.textContent = 'Next';
  nextBtn.disabled = currentPage === totalPages;
  nextBtn.addEventListener('click', () => go(currentPage + 1));
  controls.appendChild(nextBtn);
}
